import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A product gallery whose image set comes from a variant metafield.
 *
 * The set is resolved in Liquid, so on variant change this only swaps itself
 * for the markup the variant picker already fetched, as media-gallery.js does.
 *
 * Arrows and thumbnails mirror the Fotorama gallery on the live Magento PDP:
 * the stage stops at both ends and greys out the arrow there, and the strip
 * pages by a full row and disables its arrows when it runs out of room.
 * Mobile swaps arrows/thumbnails for Magento-style dots under the stage.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - The scrolling track holding the frames.
 * @property {HTMLElement[]} [frames] - The frame elements.
 * @property {HTMLButtonElement} [stagePrev] - The stage's previous arrow.
 * @property {HTMLButtonElement} [stageNext] - The stage's next arrow.
 * @property {HTMLElement} [shaft] - The scrolling thumbnail strip.
 * @property {HTMLButtonElement[]} [thumbs] - The thumbnail buttons.
 * @property {HTMLButtonElement[]} [dots] - Mobile pagination dots.
 * @property {HTMLButtonElement} [navPrev] - The thumbnail strip's previous arrow.
 * @property {HTMLButtonElement} [navNext] - The thumbnail strip's next arrow.
 *
 * @extends Component<Refs>
 */
class MgVariantGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.track?.addEventListener('scroll', this.#handleTrackScroll, { passive: true, signal });
    this.refs.shaft?.addEventListener('scroll', this.#handleShaftScroll, { passive: true, signal });
    this.#bindStageFocus(signal);

    this.#jumpToInitial();
    this.#updateStageArrows();
    this.#updateNavArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /** @returns {number} The number of frames on the stage. */
  get #count() {
    return this.refs.frames?.length ?? 0;
  }

  /** @returns {number} The index of the frame currently on screen. */
  get #current() {
    const { track } = this.refs;
    if (!track || !track.clientWidth) return 0;

    return Math.round(track.scrollLeft / track.clientWidth);
  }

  /** @returns {ScrollBehavior} Honours the visitor's reduced motion setting. */
  get #behavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * Scrolls a frame into view. Called declaratively from the thumbnails, which
   * pass the index as the leading argument ahead of the event.
   *
   * @param {number | string} index - The frame index.
   */
  select(index) {
    const { track } = this.refs;
    if (!track) return;

    const left = Number(index) * track.clientWidth;

    // Now that the ends clamp instead of wrapping, an arrow can ask for the
    // frame already on screen. Chrome leaves that smooth scroll pending and
    // swallows the next one, so skip it outright.
    if (Math.abs(track.scrollLeft - left) < 1) return;

    track.scrollTo({ left, behavior: this.#behavior });
  }

  /** Advances to the next frame, stopping on the last one. */
  next() {
    if (!this.#count) return;

    this.select(Math.min(this.#current + 1, this.#count - 1));
  }

  /** Steps back a frame, stopping on the first one. */
  previous() {
    if (!this.#count) return;

    this.select(Math.max(this.#current - 1, 0));
  }

  /**
   * Pages the thumbnail strip by a full row.
   *
   * @param {number | string} direction - 1 to page forwards, -1 to page back.
   */
  scrollThumbnails(direction) {
    const { shaft } = this.refs;
    if (!shaft) return;

    shaft.scrollBy({ left: Number(direction) * shaft.clientWidth, behavior: this.#behavior });
  }

  /**
   * Promotes the images that will be on screen the instant this markup lands.
   *
   * Lazy is right on first load but wrong on a swap, where the gallery arrives
   * before its pictures. These nodes are still detached, so the hints land
   * before the first layout. Only the opening frame is promoted, so nobody pays
   * for frames they never scroll to.
   *
   * @param {Element} gallery - The freshly rendered gallery, before insertion.
   */
  #primeImages(gallery) {
    const index = Number(gallery.dataset.initialIndex ?? 0);
    const frames = gallery.querySelectorAll('.mg-variant-gallery__frame img');
    const opening = frames[index] ?? frames[0];

    if (opening) {
      opening.setAttribute('loading', 'eager');
      opening.setAttribute('fetchpriority', 'high');
    }

    // All on screen together at 90px, so lazy just makes the strip fill in.
    for (const thumb of gallery.querySelectorAll('.mg-variant-gallery__thumb img')) {
      thumb.setAttribute('loading', 'eager');
    }
  }

  /**
   * Opens on the frame Liquid picked. The position is set rather than scrolled
   * to, so the gallery lands on the photo instead of sliding there.
   */
  #jumpToInitial() {
    const index = Number(this.dataset.initialIndex ?? 0);
    if (!index) return;

    const { track } = this.refs;
    if (!track) return;

    const apply = () => {
      if (!track.clientWidth) return;

      track.scrollLeft = index * track.clientWidth;
      this.#handleTrackScroll();
    };

    // Freshly inserted markup has no layout yet, and a zero width would land
    // the gallery back on the first frame.
    if (track.clientWidth) apply();
    else requestAnimationFrame(apply);
  }

  /** Marks the thumbnail/dot for the frame on screen and keeps the strip in view. */
  #handleTrackScroll = () => {
    this.#updateStageArrows();

    const { thumbs, shaft, dots } = this.refs;
    const current = this.#current;

    if (dots?.length) {
      for (const [index, dot] of dots.entries()) {
        dot.setAttribute('aria-current', index === current ? 'true' : 'false');
      }
    }

    if (!thumbs?.length) return;

    for (const [index, thumb] of thumbs.entries()) {
      thumb.setAttribute('aria-current', index === current ? 'true' : 'false');
    }

    const active = thumbs[current];
    if (!active || !shaft) return;

    // Only nudge the strip when the active thumbnail has scrolled out of it,
    // so paging through the stage does not fight the visitor's own scrolling.
    const activeBox = active.getBoundingClientRect();
    const shaftBox = shaft.getBoundingClientRect();

    if (activeBox.left < shaftBox.left || activeBox.right > shaftBox.right) {
      shaft.scrollTo({ left: active.offsetLeft, behavior: this.#behavior });
    }
  };

  #handleShaftScroll = () => {
    this.#updateNavArrows();
  };

  /**
   * Touch has no hover, so a tap on the photo focuses the stage and the rails
   * show. A tap outside the gallery blurs it and they slide away, as on desktop.
   *
   * @param {AbortSignal} signal
   */
  #bindStageFocus(signal) {
    const stage = this.querySelector('.mg-variant-gallery__stage');
    if (!(stage instanceof HTMLElement)) return;

    stage.tabIndex = -1;

    stage.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target;
        if (target instanceof Element && target.closest('.mg-variant-gallery__arr')) return;

        stage.focus({ preventScroll: true });
      },
      { signal }
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target;
        if (!(target instanceof Node) || this.contains(target)) return;

        const active = document.activeElement;
        if (active instanceof HTMLElement && this.contains(active)) active.blur();
      },
      { signal }
    );
  }

  /** Disables a stage arrow when there is no frame left in that direction. */
  #updateStageArrows() {
    const { stagePrev, stageNext } = this.refs;
    const current = this.#current;

    stagePrev?.toggleAttribute('disabled', current <= 0);
    stageNext?.toggleAttribute('disabled', current >= this.#count - 1);
  }

  /** Disables a strip arrow when the strip cannot move any further that way. */
  #updateNavArrows() {
    const { shaft, navPrev, navNext } = this.refs;
    if (!shaft) return;

    // Sub-pixel scroll widths round the wrong way at some zoom levels.
    const remaining = shaft.scrollWidth - shaft.clientWidth - shaft.scrollLeft;

    navPrev?.toggleAttribute('disabled', shaft.scrollLeft <= 1);
    navNext?.toggleAttribute('disabled', remaining <= 1);
  }

  /**
   * Replaces this gallery with the one rendered for the newly selected variant.
   *
   * @param {import('@shopify/events').ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const newGallery = detail?.html?.querySelector('mg-variant-gallery');
        if (!newGallery) return;

        this.#primeImages(newGallery);
        this.replaceWith(newGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[mg-variant-gallery] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('mg-variant-gallery')) {
  customElements.define('mg-variant-gallery', MgVariantGallery);
}
